import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { Tag } from '../types/audience.types';
import { separateTags } from '../logic/audienceBuilder.logic';
import { buildProducts, ProductOption } from '@/lib/helpers';
import { useDefaultStore } from '@/stores/defaultStore';
import { useStoreStore } from '@/stores/storeStore';
import { AUDIENCE_DEFAULTS } from '../constants/audienceDefaults';
import { useRecipientCount } from '../queries';
import { useModal } from '@/hooks/useModal';

interface UseAudienceSelectionProps {
    initialTags?: Tag[];
    setValue?: UseFormSetValue<any>;
}

export const useAudienceSelection = ({ initialTags = [], setValue }: UseAudienceSelectionProps = {}) => {
    const { store } = useDefaultStore();
    const getFunnels = useStoreStore(state => state.getFunnels);

    const [includeTags, setIncludeTags] = useState<Tag[]>([]);
    const [excludeTags, setExcludeTags] = useState<Tag[]>([]);
    const [excludeToggle, setExcludeToggle] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(AUDIENCE_DEFAULTS.INITIAL_PAGE);

    // Build product options from store data (including funnels, excluding sections to match Vue)
    const productOptions = useMemo<ProductOption[]>(() => {
        if (!store) return [];
        const funnels = getFunnels();
        return buildProducts({
            store,
            funnels,
            format: 'tags',
        }) as ProductOption[];
    }, [store, getFunnels]);

    // Initialize tags from props (only on mount to avoid infinite loops)
    const initializeTags = useEffectEvent((tags: Tag[]) => {
        const { includeTags: included, excludeTags: excluded } = separateTags(tags);
        setIncludeTags(included);
        setExcludeTags(excluded);
        setExcludeToggle(excluded.length > 0);
    });

    useEffect(() => {
        if (initialTags.length > 0) {
            initializeTags(initialTags);
        }
    }, []);

    // Fetch recipient count and fan list
    const { recipientCount, fans, isLoading } = useRecipientCount({
        includeTags,
        excludeTags,
    });

    // Reset to first page when tags change
    const resetPage = useEffectEvent(() => {
        setCurrentPage(AUDIENCE_DEFAULTS.INITIAL_PAGE);
    });

    useEffect(() => {
        resetPage();
    }, [includeTags, excludeTags]);

    // Pagination
    const totalPages = Math.ceil(recipientCount / AUDIENCE_DEFAULTS.PAGE_SIZE);
    const paginatedFans = fans.slice(
        (currentPage - 1) * AUDIENCE_DEFAULTS.PAGE_SIZE,
        currentPage * AUDIENCE_DEFAULTS.PAGE_SIZE
    );

    // Modal state
    const { isOpen: isModalOpen, open: openModal, close: closeModal } = useModal();
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

    const updateFormValue = useEffectEvent((tags: Tag[]) => {
        setValue?.(
            'tags',
            tags.filter(t => !t.excluded)
        );
    });

    const handleConfirm = (tags: Tag[], _count: number) => {
        setSelectedTags(tags);

        // Separate tags into include and exclude
        const { includeTags: included, excludeTags: excluded } = separateTags(tags);
        setIncludeTags(included);
        setExcludeTags(excluded);
        setExcludeToggle(excluded.length > 0);

        // Update form value if setValue is provided
        updateFormValue(tags);
        closeModal();
    };

    const clearFormValue = useEffectEvent(() => {
        setValue?.('tags', []);
    });

    const handleDelete = () => {
        setSelectedTags([]);
        setIncludeTags([]);
        setExcludeTags([]);
        setExcludeToggle(false);
        clearFormValue();
    };

    // Initialize selected tags from initial tags (only on mount to avoid infinite loops)
    const initializeSelectedTags = useEffectEvent((tags: Tag[]) => {
        setSelectedTags(tags);
    });

    useEffect(() => {
        if (initialTags.length > 0) {
            initializeSelectedTags(initialTags);
        }
    }, []);

    return {
        includeTags,
        setIncludeTags,
        excludeTags,
        setExcludeTags,
        excludeToggle,
        setExcludeToggle,
        fans: paginatedFans,
        recipientCount,
        isLoading,
        currentPage,
        setCurrentPage,
        totalPages,
        productOptions,

        // Modal state and handlers
        isModalOpen,
        selectedTags,
        openModal,
        closeModal,
        handleConfirm,
        handleDelete,
    };
};
